'use client';

import { Icon } from '@/general/Icons/Icon';
import clsx from 'clsx';
import { FC, useEffect, useRef } from 'react';

import { AccordionItem } from './Accordion.type';

interface AccordionProps {
  items: AccordionItem[];
  initialOpenIndexes?: number[];
  handleAccordionToggle?: (isOpen: boolean) => void;
}

export const Accordion: FC<AccordionProps> = ({ items, initialOpenIndexes = [], handleAccordionToggle }) => (
  <div>
    {items.map(({ id, header, content }, index) => {
      const initialOpenIndex = initialOpenIndexes.findIndex((value) => value === index);

      return (
        <AccordionSingle
          key={id}
          id={id}
          header={header}
          content={content}
          initialOpen={initialOpenIndex >= 0}
          handleAccordionToggle={handleAccordionToggle}
        />
      );
    })}
  </div>
);

interface AccordionItemProps extends AccordionItem {
  initialOpen?: boolean;
  handleAccordionToggle?: (isOpen: boolean) => void;
  testId?: string;
}

export const AccordionSingle: FC<AccordionItemProps> = ({
  id,
  header,
  content,
  initialOpen = false,
  testId,
  handleAccordionToggle,
}) => {
  const ref = useRef<HTMLDetailsElement>(null);
  const idHeader = `accordion-${id}-header`;
  const accordionTestId = testId || (id === 'driveType' ? 'filter-drivetrain' : undefined);

  useEffect(() => {
    const handleToggle = () => {
      if (handleAccordionToggle) {
        handleAccordionToggle(ref.current?.open ?? false);
      }
    };

    if (ref.current) {
      ref.current.addEventListener('toggle', handleToggle);
    }

    return () => {
      if (ref.current) {
        ref.current.removeEventListener('toggle', handleToggle);
      }
    };
  }, [handleAccordionToggle]);

  return (
    <details
      ref={ref}
      className={clsx(
        'group border-b border-bor-neu-mut',
        // Styles for the content animation
        'details-content:-translate-y-4 details-content:opacity-0 details-content:transition-all details-content:transition-discrete details-content:duration-300 open:details-content:translate-y-0 open:details-content:opacity-100',
      )}
      id={idHeader}
      data-testid={accordionTestId}
      open={initialOpen}
    >
      <summary className="flex cursor-pointer items-center justify-between gap-2 px-4 py-3">
        <div className="c-fs-body1 flex-1 font-extrabold">{header}</div>
        <div className="transition-transform duration-200 group-open:rotate-180">
          <Icon name="caret-down" />
        </div>
      </summary>
      <div className="c-fs-body2 px-4 pt-2 pb-4 font-normal">{content}</div>
    </details>
  );
};
